import type { ReactNode } from "react";
import type { PlatformApiClient } from "./api";
import { CustomProduct } from "./CustomProduct";
import { MenuProduct } from "./MenuProduct";
import { ReputationProduct } from "./ReputationProduct";
import { SalonsProduct } from "./salons/SalonsProduct";
import { CommerceProduct } from "./commerce/CommerceProduct";
import { HospitalityProduct } from "./hospitality/HospitalityProduct";
import { ContenedoresProduct } from "./contenedores/ContenedoresProduct";
import type { OrganizationContext, PlatformUser } from "./types";

export interface ProductRuntimeProps {
  organization: OrganizationContext;
  nestedPath: string;
  api: PlatformApiClient;
  navigate: (path: string) => void;
  onExpired: () => void;
  user?: PlatformUser;
}

export interface ProductRuntimeDefinition {
  routeBase: string;
  render: (props: ProductRuntimeProps) => ReactNode;
}

const PRODUCT_RUNTIMES: readonly ProductRuntimeDefinition[] = Object.freeze([
  Object.freeze({
    routeBase: "reputation",
    render: (props: ProductRuntimeProps) => (
      <ReputationProduct
        key={`${props.organization.organization.id}:${props.user?.id ?? ""}`}
        organization={props.organization}
        nestedPath={props.nestedPath}
        api={props.api}
        navigate={props.navigate}
        onExpired={props.onExpired}
        user={props.user}
      />
    ),
  }),
  Object.freeze({
    routeBase: "menu",
    render: (props: ProductRuntimeProps) => (
      <MenuProduct
        organization={props.organization}
        api={props.api}
        onExpired={props.onExpired}
      />
    ),
  }),
  Object.freeze({
    routeBase: "custom",
    render: (props: ProductRuntimeProps) => (
      <CustomProduct
        organization={props.organization}
        nestedPath={props.nestedPath}
        api={props.api}
        navigate={props.navigate}
      />
    ),
  }),
  Object.freeze({
    routeBase: "commerce",
    render: (props: ProductRuntimeProps) => (
      <CommerceProduct
        key={`${props.organization.organization.id}:${props.user?.id ?? ""}`}
        organization={props.organization}
        nestedPath={props.nestedPath}
        api={props.api}
        navigate={props.navigate}
        onExpired={props.onExpired}
      />
    ),
  }),
  Object.freeze({
    routeBase: "hospitality",
    render: (props: ProductRuntimeProps) => (
      <HospitalityProduct
        organization={props.organization}
        nestedPath={props.nestedPath}
        api={props.api}
        onExpired={props.onExpired}
      />
    ),
  }),
  Object.freeze({
    routeBase: "contenedores",
    render: (props: ProductRuntimeProps) => (
      <ContenedoresProduct
        key={props.organization.organization.id}
        organization={props.organization}
        nestedPath={props.nestedPath}
        api={props.api}
        navigate={props.navigate}
        onExpired={props.onExpired}
      />
    ),
  }),
  Object.freeze({
    routeBase: "salons",
    render: (props: ProductRuntimeProps) => (
      <SalonsProduct
        organization={props.organization}
        nestedPath={props.nestedPath}
        api={props.api}
        navigate={props.navigate}
        onExpired={props.onExpired}
      />
    ),
  }),
]);

const runtimeByRoute = new Map(
  PRODUCT_RUNTIMES.map((runtime) => [runtime.routeBase, runtime] as const),
);

export function hasProductRuntime(routeBase: string): boolean {
  return runtimeByRoute.has(routeBase);
}

export function renderProductRuntime(
  routeBase: string,
  props: ProductRuntimeProps,
): ReactNode | null {
  return runtimeByRoute.get(routeBase)?.render(props) ?? null;
}

export function productRuntimeRouteBases(): readonly string[] {
  return Object.freeze(PRODUCT_RUNTIMES.map((runtime) => runtime.routeBase));
}
